import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunk,
  createAxiosAsyncThunkWithParams,
  type DynamicUrl,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export interface ShortcutState {
  shortcuts: ShortcutShape[]
  loading: LoadingStates
  submitLoading: LoadingStates
  deleteLoading: LoadingStates
  optionsLoading: LoadingStates
}

const initialState: ShortcutState = {
  shortcuts: [],
  loading: 'idle',
  submitLoading: 'idle',
  deleteLoading: 'idle',
  optionsLoading: 'idle'
}

export interface ShortcutShape {
  id: number
  title_fi: string
  link: string
  description_fi: string | null
  order: number
}

export interface ShortcutDetailShape {
  id: number | null
  title_fi: string
  link: string | null
  description_fi: string | null
  order: number
}

type FetchShortcutsResponse = ShortcutShape[]

const fetchShortcuts = createAxiosAsyncThunk<FetchShortcutsResponse, undefined>(
  'shortcut/fetchShortcuts',
  '/api/v1/sitecontent/front-page-shortcuts/'
)

type CreateShortcutRequest = ShortcutDetailShape

type CreateShortcutResponse = ShortcutDetailShape

const createShortcut = createAsyncThunk<
  CreateShortcutResponse,
  CreateShortcutRequest,
  { rejectValue: ValidationErrors }
>('shortcut/createShortcut', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateShortcutResponse>(
      `/api/v1/sitecontent/front-page-shortcuts/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type ChangeShortcutRequest = ShortcutDetailShape

type ChangeShortcutResponse = ShortcutDetailShape

const changeShortcut = createAsyncThunk<
  ChangeShortcutResponse,
  ChangeShortcutRequest,
  {
    rejectValue: ValidationErrors
  }
>('shortcut/changeShortcut', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeShortcutResponse>(
      `/api/v1/sitecontent/front-page-shortcuts/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteShortcutResponse = Record<string, never>

const deleteShortcut = createAxiosAsyncThunkWithParams<DeleteShortcutResponse, DynamicUrl>(
  'shortcut/deleteShortcut',
  '/api/v1/sitecontent/front-page-shortcuts',
  'DELETE'
)

const genericActions = actionGenerator('shortcut')

const shortcutSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchShortcuts.fulfilled, (state, { payload }) => {
        state.shortcuts = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchShortcuts.pending, state => {
        state.shortcuts = initialState.shortcuts
        state.loading = 'pending'
      })
      .addCase(fetchShortcuts.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(createShortcut.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(createShortcut.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createShortcut.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeShortcut.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeShortcut.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(changeShortcut.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteShortcut.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteShortcut.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteShortcut.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default shortcutSlice.reducer

export { fetchShortcuts, createShortcut, changeShortcut, deleteShortcut }
